/**
 * Expanded solar system catalog for SkyTrek.
 * Radii in km, distances in AU (heliocentric for planets/dwarfs;
 * planet-centric km for moons converted to AU).
 */

export type BodyKind =
  | 'star'
  | 'planet'
  | 'dwarf'
  | 'moon'
  | 'asteroid'
  | 'belt';

export interface BodyDef {
  id: string;
  name: string;
  aliases: string[];
  kind: BodyKind;
  /** Mean radius km */
  radiusKm: number;
  /** Heliocentric semi-major axis AU (moons: ignored; use parent + orbitKm) */
  semiMajorAu: number;
  /** For moons: orbital distance from parent in km */
  orbitKm?: number;
  parent?: string;
  /** Orbital inclination degrees */
  inclination: number;
  /** Orbital period days (visual animation) */
  periodDays: number;
  /** Mean longitude phase offset radians */
  phase: number;
  eccentricity: number;
  color: string;
  emissive?: string;
  hasAtmosphere?: boolean;
  atmosphereColor?: string;
  atmosphereScale?: number;
  hasRings?: boolean;
  ringInner?: number;
  ringOuter?: number;
  ringColor?: string;
  textureKey?: string;
  cloudKey?: string;
  /** Axial tilt degrees */
  tilt?: number;
  /** Rotation period hours (visual spin) */
  rotationHours?: number;
  description?: string;
}

const KM_PER_AU = 149_597_870.7;

export function moonAuFromParent(orbitKm: number): number {
  return orbitKm / KM_PER_AU;
}

export const BODIES: BodyDef[] = [
  // —— Star ——
  {
    id: 'sun',
    name: 'Sun',
    aliases: ['sol', 'star'],
    kind: 'star',
    radiusKm: 696_340,
    semiMajorAu: 0,
    inclination: 0,
    periodDays: 0,
    phase: 0,
    eccentricity: 0,
    color: '#ffcc66',
    emissive: '#ffaa33',
    textureKey: 'sun',
    rotationHours: 609.12,
    description: 'G-type main-sequence star',
  },

  // —— Planets ——
  {
    id: 'mercury',
    name: 'Mercury',
    aliases: [],
    kind: 'planet',
    radiusKm: 2439.7,
    semiMajorAu: 0.387,
    inclination: 7.0,
    periodDays: 87.97,
    phase: 0.4,
    eccentricity: 0.205,
    color: '#9e9e9e',
    textureKey: 'mercury',
    tilt: 0.034,
    rotationHours: 1407.6,
    description: 'Terrestrial planet',
  },
  {
    id: 'venus',
    name: 'Venus',
    aliases: [],
    kind: 'planet',
    radiusKm: 6051.8,
    semiMajorAu: 0.723,
    inclination: 3.4,
    periodDays: 224.7,
    phase: 1.2,
    eccentricity: 0.007,
    color: '#e8cda0',
    textureKey: 'venus',
    hasAtmosphere: true,
    atmosphereColor: '#e8d4a8',
    atmosphereScale: 1.03,
    tilt: 177.4,
    rotationHours: -5832.5,
    description: 'Terrestrial planet · dense atmosphere',
  },
  {
    id: 'earth',
    name: 'Earth',
    aliases: ['terra', 'world'],
    kind: 'planet',
    radiusKm: 6371,
    semiMajorAu: 1.0,
    inclination: 0,
    periodDays: 365.25,
    phase: 2.1,
    eccentricity: 0.017,
    color: '#4a7ab5',
    textureKey: 'earth',
    cloudKey: 'earthClouds',
    hasAtmosphere: true,
    atmosphereColor: '#6eb6ff',
    atmosphereScale: 1.025,
    tilt: 23.4,
    rotationHours: 23.93,
    description: 'Terrestrial planet · home',
  },
  {
    id: 'mars',
    name: 'Mars',
    aliases: ['red planet'],
    kind: 'planet',
    radiusKm: 3389.5,
    semiMajorAu: 1.524,
    inclination: 1.85,
    periodDays: 686.98,
    phase: 3.0,
    eccentricity: 0.093,
    color: '#c1440e',
    textureKey: 'mars',
    hasAtmosphere: true,
    atmosphereColor: '#e8a080',
    atmosphereScale: 1.02,
    tilt: 25.2,
    rotationHours: 24.62,
    description: 'Terrestrial planet',
  },
  {
    id: 'jupiter',
    name: 'Jupiter',
    aliases: ['jovian'],
    kind: 'planet',
    radiusKm: 69_911,
    semiMajorAu: 5.203,
    inclination: 1.3,
    periodDays: 4332.59,
    phase: 0.8,
    eccentricity: 0.048,
    color: '#c88b3a',
    textureKey: 'jupiter',
    hasAtmosphere: true,
    atmosphereColor: '#d4a574',
    atmosphereScale: 1.015,
    tilt: 3.1,
    rotationHours: 9.93,
    description: 'Gas giant',
  },
  {
    id: 'saturn',
    name: 'Saturn',
    aliases: [],
    kind: 'planet',
    radiusKm: 58_232,
    semiMajorAu: 9.537,
    inclination: 2.49,
    periodDays: 10_759.22,
    phase: 4.2,
    eccentricity: 0.054,
    color: '#e6d5a8',
    textureKey: 'saturn',
    hasAtmosphere: true,
    atmosphereColor: '#e8dcc0',
    atmosphereScale: 1.015,
    hasRings: true,
    ringInner: 1.11,
    ringOuter: 2.27,
    ringColor: '#c9b896',
    tilt: 26.7,
    rotationHours: 10.66,
    description: 'Gas giant · ring system',
  },
  {
    id: 'uranus',
    name: 'Uranus',
    aliases: [],
    kind: 'planet',
    radiusKm: 25_362,
    semiMajorAu: 19.191,
    inclination: 0.77,
    periodDays: 30_688.5,
    phase: 5.1,
    eccentricity: 0.047,
    color: '#b0e0e6',
    textureKey: 'uranus',
    hasAtmosphere: true,
    atmosphereColor: '#a8d8e0',
    atmosphereScale: 1.02,
    hasRings: true,
    ringInner: 1.4,
    ringOuter: 2.0,
    ringColor: '#9ab0b8',
    tilt: 97.8,
    rotationHours: -17.24,
    description: 'Ice giant',
  },
  {
    id: 'neptune',
    name: 'Neptune',
    aliases: [],
    kind: 'planet',
    radiusKm: 24_622,
    semiMajorAu: 30.07,
    inclination: 1.77,
    periodDays: 60_182,
    phase: 1.5,
    eccentricity: 0.009,
    color: '#3f54ba',
    textureKey: 'neptune',
    hasAtmosphere: true,
    atmosphereColor: '#5b7fd4',
    atmosphereScale: 1.02,
    hasRings: true,
    ringInner: 1.5,
    ringOuter: 2.5,
    ringColor: '#6a7aaa',
    tilt: 28.3,
    rotationHours: 16.11,
    description: 'Ice giant',
  },

  // —— Dwarf planets ——
  {
    id: 'ceres',
    name: 'Ceres',
    aliases: [],
    kind: 'dwarf',
    radiusKm: 473,
    semiMajorAu: 2.766,
    inclination: 10.6,
    periodDays: 1681.6,
    phase: 2.7,
    eccentricity: 0.076,
    color: '#a0a0a0',
    textureKey: 'ceres',
    tilt: 4,
    rotationHours: 9.07,
    description: 'Dwarf planet · asteroid belt',
  },
  {
    id: 'pluto',
    name: 'Pluto',
    aliases: [],
    kind: 'dwarf',
    radiusKm: 1188.3,
    semiMajorAu: 39.48,
    inclination: 17.16,
    periodDays: 90_560,
    phase: 0.3,
    eccentricity: 0.249,
    color: '#c4a882',
    textureKey: 'pluto',
    hasAtmosphere: true,
    atmosphereColor: '#c8b090',
    atmosphereScale: 1.04,
    tilt: 122.5,
    rotationHours: -153.3,
    description: 'Dwarf planet · Kuiper belt',
  },
  {
    id: 'haumea',
    name: 'Haumea',
    aliases: [],
    kind: 'dwarf',
    radiusKm: 816,
    semiMajorAu: 43.13,
    inclination: 28.2,
    periodDays: 103_468,
    phase: 3.6,
    eccentricity: 0.191,
    color: '#d8d0c8',
    textureKey: 'haumea',
    tilt: 126,
    rotationHours: 3.92,
    description: 'Dwarf planet · elongated',
  },
  {
    id: 'makemake',
    name: 'Makemake',
    aliases: [],
    kind: 'dwarf',
    radiusKm: 715,
    semiMajorAu: 45.79,
    inclination: 29.0,
    periodDays: 112_897,
    phase: 4.8,
    eccentricity: 0.159,
    color: '#c8b090',
    textureKey: 'makemake',
    tilt: 0,
    rotationHours: 22.83,
    description: 'Dwarf planet · Kuiper belt',
  },
  {
    id: 'eris',
    name: 'Eris',
    aliases: [],
    kind: 'dwarf',
    radiusKm: 1163,
    semiMajorAu: 67.86,
    inclination: 44.0,
    periodDays: 203_830,
    phase: 1.1,
    eccentricity: 0.436,
    color: '#e0e0e8',
    textureKey: 'eris',
    tilt: 0,
    rotationHours: 25.9,
    description: 'Dwarf planet · scattered disc',
  },

  // —— Earth moons ——
  {
    id: 'luna',
    name: 'Moon',
    aliases: ['luna', 'earth moon', "earth's moon"],
    kind: 'moon',
    radiusKm: 1737.4,
    semiMajorAu: 0,
    orbitKm: 384_400,
    parent: 'earth',
    inclination: 5.14,
    periodDays: 27.32,
    phase: 0.5,
    eccentricity: 0.055,
    color: '#c0c0c0',
    textureKey: 'moon',
    tilt: 6.7,
    rotationHours: 655.7,
    description: 'Natural satellite of Earth',
  },

  // —— Mars moons ——
  {
    id: 'phobos',
    name: 'Phobos',
    aliases: [],
    kind: 'moon',
    radiusKm: 11.3,
    semiMajorAu: 0,
    orbitKm: 9376,
    parent: 'mars',
    inclination: 1.1,
    periodDays: 0.319,
    phase: 0.2,
    eccentricity: 0.015,
    color: '#8a7a6a',
    textureKey: 'phobos',
    rotationHours: 7.66,
    description: 'Martian moon',
  },
  {
    id: 'deimos',
    name: 'Deimos',
    aliases: [],
    kind: 'moon',
    radiusKm: 6.2,
    semiMajorAu: 0,
    orbitKm: 23_463,
    parent: 'mars',
    inclination: 1.8,
    periodDays: 1.263,
    phase: 1.0,
    eccentricity: 0.0003,
    color: '#9a8a7a',
    textureKey: 'deimos',
    rotationHours: 30.3,
    description: 'Martian moon',
  },

  // —— Jupiter moons (expanded) ——
  {
    id: 'io',
    name: 'Io',
    aliases: [],
    kind: 'moon',
    radiusKm: 1821.6,
    semiMajorAu: 0,
    orbitKm: 421_700,
    parent: 'jupiter',
    inclination: 0.05,
    periodDays: 1.769,
    phase: 0.1,
    eccentricity: 0.004,
    color: '#e8d060',
    textureKey: 'io',
    rotationHours: 42.5,
    description: 'Galilean moon · volcanic',
  },
  {
    id: 'europa',
    name: 'Europa',
    aliases: [],
    kind: 'moon',
    radiusKm: 1560.8,
    semiMajorAu: 0,
    orbitKm: 671_034,
    parent: 'jupiter',
    inclination: 0.47,
    periodDays: 3.551,
    phase: 1.4,
    eccentricity: 0.009,
    color: '#d4c8a8',
    textureKey: 'europa',
    hasAtmosphere: true,
    atmosphereColor: '#c8d8e8',
    atmosphereScale: 1.01,
    rotationHours: 85.2,
    description: 'Galilean moon · ice shell',
  },
  {
    id: 'ganymede',
    name: 'Ganymede',
    aliases: [],
    kind: 'moon',
    radiusKm: 2634.1,
    semiMajorAu: 0,
    orbitKm: 1_070_412,
    parent: 'jupiter',
    inclination: 0.2,
    periodDays: 7.155,
    phase: 2.5,
    eccentricity: 0.001,
    color: '#a89880',
    textureKey: 'ganymede',
    rotationHours: 171.7,
    description: 'Largest moon in the solar system',
  },
  {
    id: 'callisto',
    name: 'Callisto',
    aliases: [],
    kind: 'moon',
    radiusKm: 2410.3,
    semiMajorAu: 0,
    orbitKm: 1_882_709,
    parent: 'jupiter',
    inclination: 0.19,
    periodDays: 16.69,
    phase: 3.8,
    eccentricity: 0.007,
    color: '#6a6058',
    textureKey: 'callisto',
    rotationHours: 400.5,
    description: 'Galilean moon · cratered',
  },
  {
    id: 'amalthea',
    name: 'Amalthea',
    aliases: [],
    kind: 'moon',
    radiusKm: 83.5,
    semiMajorAu: 0,
    orbitKm: 181_366,
    parent: 'jupiter',
    inclination: 0.37,
    periodDays: 0.498,
    phase: 0.7,
    eccentricity: 0.003,
    color: '#a05040',
    textureKey: 'rocky',
    rotationHours: 11.95,
    description: 'Inner Jovian moon',
  },
  {
    id: 'himalia',
    name: 'Himalia',
    aliases: [],
    kind: 'moon',
    radiusKm: 85,
    semiMajorAu: 0,
    orbitKm: 11_461_000,
    parent: 'jupiter',
    inclination: 27.5,
    periodDays: 250.6,
    phase: 4.2,
    eccentricity: 0.16,
    color: '#8a8070',
    textureKey: 'rocky',
    rotationHours: 7.78,
    description: 'Irregular Jovian moon',
  },
  {
    id: 'thebe',
    name: 'Thebe',
    aliases: [],
    kind: 'moon',
    radiusKm: 49.3,
    semiMajorAu: 0,
    orbitKm: 221_889,
    parent: 'jupiter',
    inclination: 1.08,
    periodDays: 0.675,
    phase: 1.9,
    eccentricity: 0.018,
    color: '#9a7060',
    textureKey: 'rocky',
    rotationHours: 16.2,
    description: 'Inner Jovian moon',
  },

  // —— Saturn moons ——
  {
    id: 'mimas',
    name: 'Mimas',
    aliases: [],
    kind: 'moon',
    radiusKm: 198.2,
    semiMajorAu: 0,
    orbitKm: 185_539,
    parent: 'saturn',
    inclination: 1.57,
    periodDays: 0.942,
    phase: 0.3,
    eccentricity: 0.02,
    color: '#c8c8c8',
    textureKey: 'mimas',
    rotationHours: 22.6,
    description: 'Saturnian moon · Herschel crater',
  },
  {
    id: 'enceladus',
    name: 'Enceladus',
    aliases: [],
    kind: 'moon',
    radiusKm: 252.1,
    semiMajorAu: 0,
    orbitKm: 237_948,
    parent: 'saturn',
    inclination: 0.01,
    periodDays: 1.37,
    phase: 1.1,
    eccentricity: 0.005,
    color: '#f0f4f8',
    textureKey: 'enceladus',
    hasAtmosphere: true,
    atmosphereColor: '#e8f0f8',
    atmosphereScale: 1.03,
    rotationHours: 32.9,
    description: 'Icy moon · plumes',
  },
  {
    id: 'tethys',
    name: 'Tethys',
    aliases: [],
    kind: 'moon',
    radiusKm: 531.1,
    semiMajorAu: 0,
    orbitKm: 294_619,
    parent: 'saturn',
    inclination: 1.12,
    periodDays: 1.888,
    phase: 2.0,
    eccentricity: 0.0001,
    color: '#d8d8d0',
    textureKey: 'tethys',
    rotationHours: 45.3,
    description: 'Saturnian moon',
  },
  {
    id: 'dione',
    name: 'Dione',
    aliases: [],
    kind: 'moon',
    radiusKm: 561.4,
    semiMajorAu: 0,
    orbitKm: 377_396,
    parent: 'saturn',
    inclination: 0.02,
    periodDays: 2.737,
    phase: 2.8,
    eccentricity: 0.002,
    color: '#c8c8c0',
    textureKey: 'dione',
    rotationHours: 65.7,
    description: 'Saturnian moon',
  },
  {
    id: 'rhea',
    name: 'Rhea',
    aliases: [],
    kind: 'moon',
    radiusKm: 763.8,
    semiMajorAu: 0,
    orbitKm: 527_108,
    parent: 'saturn',
    inclination: 0.35,
    periodDays: 4.518,
    phase: 3.5,
    eccentricity: 0.001,
    color: '#b8b8b0',
    textureKey: 'rhea',
    rotationHours: 108.4,
    description: 'Saturnian moon',
  },
  {
    id: 'titan',
    name: 'Titan',
    aliases: [],
    kind: 'moon',
    radiusKm: 2574.7,
    semiMajorAu: 0,
    orbitKm: 1_221_870,
    parent: 'saturn',
    inclination: 0.35,
    periodDays: 15.95,
    phase: 4.4,
    eccentricity: 0.029,
    color: '#c8a050',
    textureKey: 'titan',
    hasAtmosphere: true,
    atmosphereColor: '#e0b860',
    atmosphereScale: 1.05,
    rotationHours: 382.7,
    description: 'Largest Saturnian moon · thick atmosphere',
  },
  {
    id: 'iapetus',
    name: 'Iapetus',
    aliases: [],
    kind: 'moon',
    radiusKm: 734.5,
    semiMajorAu: 0,
    orbitKm: 3_560_820,
    parent: 'saturn',
    inclination: 15.47,
    periodDays: 79.32,
    phase: 5.2,
    eccentricity: 0.028,
    color: '#6a5a48',
    textureKey: 'iapetus',
    rotationHours: 1903.7,
    description: 'Two-toned moon',
  },
  {
    id: 'hyperion',
    name: 'Hyperion',
    aliases: [],
    kind: 'moon',
    radiusKm: 135,
    semiMajorAu: 0,
    orbitKm: 1_481_009,
    parent: 'saturn',
    inclination: 0.43,
    periodDays: 21.28,
    phase: 0.9,
    eccentricity: 0.123,
    color: '#a09070',
    textureKey: 'rocky',
    rotationHours: 13,
    description: 'Chaotic rotator',
  },
  {
    id: 'phoebe',
    name: 'Phoebe',
    aliases: [],
    kind: 'moon',
    radiusKm: 106.5,
    semiMajorAu: 0,
    orbitKm: 12_947_780,
    parent: 'saturn',
    inclination: 175.0,
    periodDays: 550.3,
    phase: 2.2,
    eccentricity: 0.156,
    color: '#5a5048',
    textureKey: 'rocky',
    rotationHours: 9.27,
    description: 'Irregular retrograde moon',
  },

  // —— Uranus moons ——
  {
    id: 'miranda',
    name: 'Miranda',
    aliases: [],
    kind: 'moon',
    radiusKm: 235.8,
    semiMajorAu: 0,
    orbitKm: 129_900,
    parent: 'uranus',
    inclination: 4.34,
    periodDays: 1.413,
    phase: 0.4,
    eccentricity: 0.001,
    color: '#b0b0a8',
    textureKey: 'miranda',
    rotationHours: 33.9,
    description: 'Uranian moon · patchwork surface',
  },
  {
    id: 'ariel',
    name: 'Ariel',
    aliases: [],
    kind: 'moon',
    radiusKm: 578.9,
    semiMajorAu: 0,
    orbitKm: 190_900,
    parent: 'uranus',
    inclination: 0.04,
    periodDays: 2.52,
    phase: 1.3,
    eccentricity: 0.001,
    color: '#c0c0b8',
    textureKey: 'ariel',
    rotationHours: 60.5,
    description: 'Uranian moon',
  },
  {
    id: 'umbriel',
    name: 'Umbriel',
    aliases: [],
    kind: 'moon',
    radiusKm: 584.7,
    semiMajorAu: 0,
    orbitKm: 266_000,
    parent: 'uranus',
    inclination: 0.13,
    periodDays: 4.144,
    phase: 2.1,
    eccentricity: 0.004,
    color: '#606060',
    textureKey: 'umbriel',
    rotationHours: 99.5,
    description: 'Uranian moon · dark',
  },
  {
    id: 'titania',
    name: 'Titania',
    aliases: [],
    kind: 'moon',
    radiusKm: 788.4,
    semiMajorAu: 0,
    orbitKm: 436_300,
    parent: 'uranus',
    inclination: 0.08,
    periodDays: 8.706,
    phase: 3.0,
    eccentricity: 0.001,
    color: '#a8a098',
    textureKey: 'titania',
    rotationHours: 208.9,
    description: 'Largest Uranian moon',
  },
  {
    id: 'oberon',
    name: 'Oberon',
    aliases: [],
    kind: 'moon',
    radiusKm: 761.4,
    semiMajorAu: 0,
    orbitKm: 583_500,
    parent: 'uranus',
    inclination: 0.07,
    periodDays: 13.46,
    phase: 4.1,
    eccentricity: 0.001,
    color: '#989088',
    textureKey: 'oberon',
    rotationHours: 323.1,
    description: 'Uranian moon',
  },

  // —— Neptune moons ——
  {
    id: 'triton',
    name: 'Triton',
    aliases: [],
    kind: 'moon',
    radiusKm: 1353.4,
    semiMajorAu: 0,
    orbitKm: 354_759,
    parent: 'neptune',
    inclination: 156.9,
    periodDays: -5.877,
    phase: 1.0,
    eccentricity: 0.00002,
    color: '#c0b8b0',
    textureKey: 'triton',
    hasAtmosphere: true,
    atmosphereColor: '#b0c0d0',
    atmosphereScale: 1.02,
    rotationHours: -141,
    description: 'Largest Neptunian moon · retrograde',
  },
  {
    id: 'proteus',
    name: 'Proteus',
    aliases: [],
    kind: 'moon',
    radiusKm: 210,
    semiMajorAu: 0,
    orbitKm: 117_647,
    parent: 'neptune',
    inclination: 0.04,
    periodDays: 1.122,
    phase: 2.4,
    eccentricity: 0.0005,
    color: '#707068',
    textureKey: 'rocky',
    rotationHours: 26.9,
    description: 'Neptunian moon',
  },
  {
    id: 'nereid',
    name: 'Nereid',
    aliases: [],
    kind: 'moon',
    radiusKm: 170,
    semiMajorAu: 0,
    orbitKm: 5_513_400,
    parent: 'neptune',
    inclination: 7.23,
    periodDays: 360.14,
    phase: 0.6,
    eccentricity: 0.75,
    color: '#909088',
    textureKey: 'rocky',
    rotationHours: 11.5,
    description: 'Irregular Neptunian moon',
  },

  // —— Pluto system ——
  {
    id: 'charon',
    name: 'Charon',
    aliases: [],
    kind: 'moon',
    radiusKm: 606,
    semiMajorAu: 0,
    orbitKm: 19_591,
    parent: 'pluto',
    inclination: 0.0,
    periodDays: 6.387,
    phase: 0,
    eccentricity: 0.0002,
    color: '#a09890',
    textureKey: 'charon',
    rotationHours: 153.3,
    description: 'Pluto’s companion',
  },
  {
    id: 'nix',
    name: 'Nix',
    aliases: [],
    kind: 'moon',
    radiusKm: 25,
    semiMajorAu: 0,
    orbitKm: 48_694,
    parent: 'pluto',
    inclination: 0.13,
    periodDays: 24.85,
    phase: 1.2,
    eccentricity: 0.002,
    color: '#c8c0b8',
    textureKey: 'rocky',
    rotationHours: 43.9,
    description: 'Plutonian moon',
  },
  {
    id: 'hydra',
    name: 'Hydra',
    aliases: [],
    kind: 'moon',
    radiusKm: 30,
    semiMajorAu: 0,
    orbitKm: 64_738,
    parent: 'pluto',
    inclination: 0.24,
    periodDays: 38.2,
    phase: 2.5,
    eccentricity: 0.006,
    color: '#c0b8b0',
    textureKey: 'rocky',
    rotationHours: 10.3,
    description: 'Plutonian moon',
  },
  {
    id: 'kerberos',
    name: 'Kerberos',
    aliases: [],
    kind: 'moon',
    radiusKm: 12,
    semiMajorAu: 0,
    orbitKm: 57_783,
    parent: 'pluto',
    inclination: 0.4,
    periodDays: 32.17,
    phase: 3.3,
    eccentricity: 0.003,
    color: '#908880',
    textureKey: 'rocky',
    rotationHours: 5.3,
    description: 'Plutonian moon',
  },
  {
    id: 'styx',
    name: 'Styx',
    aliases: [],
    kind: 'moon',
    radiusKm: 10,
    semiMajorAu: 0,
    orbitKm: 42_656,
    parent: 'pluto',
    inclination: 0.8,
    periodDays: 20.16,
    phase: 4.0,
    eccentricity: 0.006,
    color: '#a09890',
    textureKey: 'rocky',
    rotationHours: 3.2,
    description: 'Plutonian moon',
  },
];

export function findBodies(query: string): BodyDef[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const scored: { body: BodyDef; score: number }[] = [];
  for (const b of BODIES) {
    const name = b.name.toLowerCase();
    const id = b.id.toLowerCase();
    const aliases = b.aliases.map((a) => a.toLowerCase());
    let score = -1;
    if (name === q || id === q) score = 100;
    else if (aliases.includes(q)) score = 95;
    else if (name.startsWith(q) || id.startsWith(q)) score = 80;
    else if (aliases.some((a) => a.startsWith(q))) score = 75;
    else if (name.includes(q) || id.includes(q)) score = 50;
    else if (aliases.some((a) => a.includes(q))) score = 45;
    if (score >= 0) scored.push({ body: b, score });
  }
  scored.sort((a, b) => b.score - a.score || a.body.name.localeCompare(b.body.name));
  return scored.map((s) => s.body);
}

export function getBodyById(id: string): BodyDef | undefined {
  return BODIES.find((b) => b.id === id);
}
